import { useState, useEffect } from "react";

// React redux
import { useDispatch, useSelector } from "react-redux";

// react-router components
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Material Dashboard 2 React themes
import theme from "assets/theme";

// Material Dashboard 2 React routes
import ALL_ROUTES from "routes";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav } from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";

// json web token
// eslint-disable-next-line camelcase
import jwt_decode from "jwt-decode";

// Services
import { refresh, logout } from "services/authentication/auth";
import { getAll as getAllTags } from "services/tag/tags.services";
import { getAll as getAllCourses } from "services/course/courses.services";
import { getAll as getAllLessons } from "services/lesson/lessons.services";
import { getAll as getAllUsers, getCurrent as getCurrentUser } from "services/user/users.services";

// Axios
import axios from "axios";

// Refresh token interval ref
let ref = null;

const isValidToken = (token) => {
  if (!token) return false;
  try {
    return jwt_decode(token).exp > new Date().getTime() / 1000;
  } catch (error) {
    localStorage.removeItem("token");
    return false;
  }
};

const refreshToken = () => {
  refresh({ refresh_token: localStorage.getItem("refresh_token") }).then(({ data }) => {
    localStorage.setItem("token", data.token);
    localStorage.setItem("refresh_token", data.refresh_token);
    axios.defaults.headers.common.Authorization = `Bearer ${data.token}`;
  });
};

export default function App() {
  const tags = useSelector((state) => state.tag.allTags);
  const courses = useSelector((state) => state.course.allCourses);
  const lessons = useSelector((state) => state.lesson.allLessons);
  const users = useSelector((state) => state.user.allUsers);
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, sidenavColor } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dispatchApi = useDispatch();

  const token = localStorage.getItem("token");
  const validToken = isValidToken(token);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  //  Refresh token and fetching data
  useEffect(() => {
    if (ref) clearInterval(ref);
    if (validToken) {
      if (jwt_decode(token).roles.includes("ROLE_SUBSCRIBER")) {
        logout();
        dispatchApi({ type: "LOGOUT" });
        navigate("/authentication/sign-in");
      }
      ref = setInterval(refreshToken, 60000);
      getCurrentUser(parseInt(jwt_decode(token).useridentifier, 10)).then(({ data }) =>
        dispatchApi({ type: "CURRENT_USER", data })
      );
      dispatchApi({ type: "LOGOUT" });
      getAllTags(1, dispatchApi);
      getAllCourses(1, dispatchApi);
      getAllLessons(1, dispatchApi);
      getAllUsers(1, dispatchApi);
    } else logout();
  }, [validToken]);

  useEffect(() => {
    if (validToken) {
      if (tags.length % 30 === 0 && tags.length > 0)
        if (jwt_decode(token).roles.includes("ROLE_ADMIN")) {
          getAllTags(Math.ceil(tags.length / 30) + 1, dispatchApi);
        }
    } else logout();
  }, [tags]);

  useEffect(() => {
    if (validToken) {
      if (courses.length % 30 === 0 && courses.length > 0)
        getAllCourses(Math.ceil(courses.length / 30) + 1, dispatchApi);
    } else logout();
  }, [courses]);

  useEffect(() => {
    if (validToken) {
      if (lessons.length % 30 === 0 && lessons.length > 0)
        getAllLessons(Math.ceil(lessons.length / 30) + 1, dispatchApi);
    } else logout();
  }, [lessons]);

  useEffect(() => {
    if (validToken) {
      if (users.length % 30 === 0 && users.length > 0)
        getAllUsers(Math.ceil(users.length / 30) + 1, dispatchApi);
    } else logout();
  }, [users]);

  const routes =
    token && jwt_decode(token).roles.includes("ROLE_ADMIN")
      ? ALL_ROUTES
      : ALL_ROUTES.filter((route) => route.role !== "ADMIN");

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return route.key === "sign-in" ? (
          <Route
            exact
            path={route.route}
            element={!validToken ? route.component : <Navigate to="/dashboard" />}
            key={route.key}
          />
        ) : (
          <Route
            exact
            path={route.route}
            element={validToken ? route.component : <Navigate to="/authentication/sign-in" />}
            key={route.key}
          />
        );
      }
      return null;
    });
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {validToken && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={brandWhite}
            brandName="Quiz"
            routes={routes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
          <DashboardLayout>
            {routes.findIndex((r) => r.route === pathname) !== -1 && (
              <DashboardNavbar title={routes.find((r) => r.route === pathname).name} />
            )}
            <Routes>
              {getRoutes(routes)}
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
          </DashboardLayout>
        </>
      )}
      {!validToken && (
        <Routes>
          {getRoutes(routes)}
          <Route path="*" element={<Navigate to="/authentication/sign-in" />} />
        </Routes>
      )}
    </ThemeProvider>
  );
}
